import React, { useState } from "react";
import { withRouter } from "react-router";
import { Table, Modal, Button, Tag, Spin, message } from "antd";
import CommonButton from "components/ButtonWidgets/CommonButton/CommonButton";
import { showMessageDetailModal } from "../MessageDetailModal/showMessageDetailModal";
import { PaperClipOutlined, EyeOutlined } from "@ant-design/icons";
import { getEmailCampaignDetail } from "services/emailCampaignService";
import { useSelector } from "react-redux";

function MessageTable(props) {
  const { dataList, onDetail, isDetail } = props;
  const userDataStore = useSelector((x) => x.userDataStore);
  const [recipientsModalVisible, setRecipientsModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipientsList, setRecipientsList] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [attachmentFileName, setAttachmentFileName] = useState("");
  const [loadingAttachment, setLoadingAttachment] = useState(false);

  if (dataList === undefined) return <div />;

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      let hour = date.getHours();
      const minute = String(date.getMinutes()).padStart(2, "0");
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const hourStr = String(hour).padStart(2, "0");
      return `${month}/${day}/${year} ${hourStr}:${minute} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  const handleViewBody = async (record) => {
    if (!record.campaign_id) {
      // Fallback for old format
      const formattedTime = formatDateTime(
        record.created ? new Date(record.created * 1000).toISOString() : new Date().toISOString()
      );
      await showMessageDetailModal({
        title: record.toEmail || record.subject || "Email Details",
        subject: record.subject || "",
        content: record.content || "",
        send_date: formattedTime,
        attachment: record.attachment || "",
      });
      return;
    }
    
    // For new campaigns, fetch full details and show in modal
    if (!userDataStore?.token) {
      message.error("No authentication token");
      return;
    }
    
    try {
      const detail = await getEmailCampaignDetail({
        token: userDataStore.token,
        campaign_id: record.campaign_id,
      });
      
      const formattedTime = formatDateTime(detail.created_at || new Date().toISOString());
      
      await showMessageDetailModal({
        title: `Email: ${detail.subject || "Details"}`,
        subject: detail.subject || "",
        content: detail.html || detail.content || "",
        send_date: formattedTime,
        attachment: detail.attachment ? (detail.attachment.file_name || detail.attachment) : "",
        attachmentUrl: detail.attachment?.url || "",
      });
    } catch (err) {
      message.error("Failed to load email details");
      console.error("[ViewBody] error:", err);
    }
  };

  const handleViewAttachment = async (record) => {
    setLoadingAttachment(true);
    setAttachmentModalVisible(true);
    
    // If attachment URL is already in the record, use it directly
    if (record.attachment && record.attachment.url) {
      setAttachmentUrl(record.attachment.url);
      setAttachmentFileName(record.attachment.file_name || "attachment");
      setLoadingAttachment(false);
      return;
    }
    
    // Otherwise, fetch campaign detail to get attachment URL
    if (!record.campaign_id || !userDataStore?.token) {
      message.error("Unable to load attachment");
      setAttachmentModalVisible(false);
      setLoadingAttachment(false);
      return;
    }
    
    try {
      const detail = await getEmailCampaignDetail({
        token: userDataStore.token,
        campaign_id: record.campaign_id,
      });
      
      if (detail.attachment && detail.attachment.url) {
        setAttachmentUrl(detail.attachment.url);
        setAttachmentFileName(detail.attachment.file_name || "attachment");
      } else {
        message.error("No attachment found for this campaign");
        setAttachmentModalVisible(false);
      }
    } catch (err) {
      message.error("Failed to load attachment");
      console.error("[ViewAttachment] error:", err);
      setAttachmentModalVisible(false);
    } finally {
      setLoadingAttachment(false);
    }
  };

  const handleViewRecipients = async (record) => {
    setSelectedCampaign(record);
    setRecipientsModalVisible(true);
    setLoadingRecipients(true);
    
    // Fetch full campaign detail to get recipients list
    if (record.campaign_id && userDataStore?.token) {
      try {
        const detail = await getEmailCampaignDetail({
          token: userDataStore.token,
          campaign_id: record.campaign_id,
        });
        // detail is already the campaign object after extraction in service
        if (detail && Array.isArray(detail.recipients)) {
          setRecipientsList(detail.recipients);
        } else {
          setRecipientsList([]);
        }
      } catch (err) {
        message.error("Failed to load recipients");
        setRecipientsList([]);
      } finally {
        setLoadingRecipients(false);
      }
    } else {
      setRecipientsList([]);
      setLoadingRecipients(false);
    }
  };

  // Campaign list view (Mailchimp style)
  const campaignColumns = [
    {
      title: "Type",
      key: "message_type",
      width: 80,
      render: (record) => {
        const type = record.message_type || "Email";
        return (
          <Tag color={type === "SMS" ? "blue" : "green"}>{type}</Tag>
        );
      },
    },
    {
      title: "To (Audiences)",
      key: "audience_names",
      render: (record) => {
        const names = Array.isArray(record.audience_names)
          ? record.audience_names
          : record.audience_names
          ? [record.audience_names]
          : [];
        return (
          <span
            style={{
              color: "var(--mainColor)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => handleViewRecipients(record)}
          >
            {names.length > 0 ? names.join(", ") : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "# of Recipients",
      dataIndex: "recipients_count",
      key: "recipients_count",
      render: (count) => count || 0,
    },
    {
      title: "Date",
      key: "created_at",
      render: (record) => {
        const dateStr = formatDateTime(record.created_at);
        return dateStr.split(" ")[0]; // Just date part
      },
    },
    {
      title: "Time",
      key: "created_at_time",
      render: (record) => {
        const dateStr = formatDateTime(record.created_at);
        return dateStr.split(" ").slice(1).join(" "); // Just time part
      },
    },
    {
      title: "View",
      key: "view",
      render: (record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewBody(record)}
        >
          View
        </Button>
      ),
    },
    {
      title: "Attachment",
      key: "attachment",
      render: (record) => {
        if (record.has_attachment) {
          return (
            <Button
              type="link"
              icon={<PaperClipOutlined />}
              onClick={() => handleViewAttachment(record)}
            >
              View
            </Button>
          );
        }
        return <span>-</span>;
      },
    },
  ];

  // Recipients detail view
  const recipientColumns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          sent: "green",
          queued: "orange",
          failed: "red",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status || "unknown"}
          </Tag>
        );
      },
    },
  ];

  const dataSource =
    dataList.length === 0
      ? []
      : dataList.map((data, index) => {
          return { ...data, key: index };
        });
  const paginationProps = {
    total: dataList.length,
    showSizeChanger: true,
    defaultPageSize: 20,
    pageSizeOptions: ["10", "20", "30", "50"],
    locale: { items_per_page: "" },
  };

  return (
    <>
    <div className="message-list-widget" style={{ marginTop: 20 }}>
      <Table
          columns={isDetail ? recipientColumns : campaignColumns}
        dataSource={dataSource}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        pagination={paginationProps}
      />
    </div>

      {/* Recipients Modal */}
      <Modal
        title={`Recipients: ${selectedCampaign?.subject || ""}`}
        open={recipientsModalVisible}
        onCancel={() => {
          setRecipientsModalVisible(false);
          setRecipientsList([]);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setRecipientsModalVisible(false);
              setRecipientsList([]);
            }}
          >
            Close
          </Button>,
        ]}
        width={700}
      >
        <Spin spinning={loadingRecipients}>
          {loadingRecipients ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              Loading recipients...
            </div>
          ) : recipientsList.length > 0 ? (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table
                columns={recipientColumns}
                dataSource={recipientsList.map((r, idx) => ({
                  ...r,
                  key: idx,
                }))}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                size="small"
              />
            </div>
          ) : (
            <p style={{ textAlign: "center", padding: "40px" }}>
              No recipients found for this campaign.
            </p>
          )}
        </Spin>
      </Modal>

      {/* Attachment Preview Modal */}
      <Modal
        title={`Attachment: ${attachmentFileName || "Preview"}`}
        open={attachmentModalVisible}
        onCancel={() => {
          setAttachmentModalVisible(false);
          setAttachmentUrl("");
          setAttachmentFileName("");
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setAttachmentModalVisible(false);
              setAttachmentUrl("");
              setAttachmentFileName("");
            }}
          >
            Close
          </Button>,
          attachmentUrl && (
            <Button
              key="download"
              type="primary"
              onClick={() => {
                const link = document.createElement('a');
                link.href = attachmentUrl;
                link.download = attachmentFileName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </Button>
          ),
        ]}
        width={900}
        style={{ top: 20 }}
      >
        <Spin spinning={loadingAttachment}>
          {loadingAttachment ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              Loading attachment...
            </div>
          ) : attachmentUrl ? (
            <div style={{ width: "100%", height: "600px", border: "1px solid #e8e8e8" }}>
              {attachmentFileName.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={attachmentUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="PDF Preview"
                />
              ) : attachmentFileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                <img
                  src={attachmentUrl}
                  alt={attachmentFileName}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <p>Preview not available for this file type.</p>
                  <p>Click "Download" to view the file.</p>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              No attachment found.
            </div>
          )}
        </Spin>
      </Modal>
    </>
  );
}

export default withRouter(MessageTable);
